import React, { useState, useEffect } from 'react';
import { Shield, AlertTriangle, Lock, Settings } from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';

const LoginBlocks = () => {
  const [blocks, setBlocks] = useState({
    maintenance: {
      enabled: false,
      message: 'The panel is currently undergoing maintenance. Please check back later.'
    },
    emergency: {
      enabled: false,
      message: 'Login is temporarily disabled due to an emergency. We apologize for the inconvenience.'
    },
    security: {
      enabled: false,
      message: 'Login has been disabled for security reasons. Please contact support for more information.'
    },
    custom: {
      enabled: false,
      message: 'Login is currently disabled. Please try again later.'
    }
  });

  const [editingMessage, setEditingMessage] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlockSettings();
  }, []);

  const fetchBlockSettings = async () => {
    try {
      const response = await axios.get('/api/login-blocks/settings');
      if (response.data.success) {
        setBlocks(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching login block settings:', error);
      toast.error('Failed to load login block settings');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (blockType) => {
    const newEnabled = !blocks[blockType].enabled;
    
    try {
      const response = await axios.post(
        '/api/login-blocks/update',
        {
          blockType,
          enabled: newEnabled,
          message: blocks[blockType].message
        },
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem('token')}`
          }
        }
      );

      if (response.data.success) {
        setBlocks(prev => ({
          ...prev,
          [blockType]: {
            ...prev[blockType],
            enabled: newEnabled
          }
        }));
        toast.success(`${blockType.charAt(0).toUpperCase() + blockType.slice(1)} block ${newEnabled ? 'enabled' : 'disabled'}`);
      }
    } catch (error) {
      console.error('Error updating login block:', error);
      toast.error('Failed to update login block');
    }
  };

  const handleMessageChange = (blockType, value) => {
    setEditingMessage(prev => ({
      ...prev,
      [blockType]: value
    }));
  };

  const handleSaveMessage = async (blockType) => {
    const newMessage = editingMessage[blockType];
    
    if (!newMessage || newMessage.trim() === '') {
      toast.error('Message cannot be empty');
      return;
    }

    try {
      const response = await axios.post(
        '/api/login-blocks/update',
        {
          blockType,
          enabled: blocks[blockType].enabled,
          message: newMessage
        },
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem('token')}`
          }
        }
      );

      if (response.data.success) {
        setBlocks(prev => ({
          ...prev,
          [blockType]: {
            ...prev[blockType],
            message: newMessage
          }
        }));
        setEditingMessage(prev => {
          const updated = { ...prev };
          delete updated[blockType];
          return updated;
        });
        toast.success('Message updated successfully');
      }
    } catch (error) {
      console.error('Error updating message:', error);
      toast.error('Failed to update message');
    }
  };

  const blockConfigs = [
    {
      type: 'maintenance',
      title: 'Maintenance Mode',
      description: 'Block logins during scheduled maintenance',
      icon: Settings,
      color: '#3b82f6'
    },
    {
      type: 'emergency',
      title: 'Emergency Block',
      description: 'Immediate lockdown for critical situations',
      icon: AlertTriangle,
      color: '#ef4444'
    },
    {
      type: 'security',
      title: 'Security Block',
      description: 'Block logins for security incidents',
      icon: Shield,
      color: '#f59e0b'
    },
    {
      type: 'custom',
      title: 'Custom Block',
      description: 'Custom login restriction with your own message',
      icon: Lock,
      color: '#8b5cf6'
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-100 mb-2">Login Access Control</h2>
        <p className="text-gray-400">
          Control non-admin access to the panel. Admin accounts can always login regardless of these settings.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6">
        {blockConfigs.map(config => {
          const Icon = config.icon;
          const block = blocks[config.type];
          const isEditing = editingMessage[config.type] !== undefined;
          const displayMessage = isEditing ? editingMessage[config.type] : block.message;

          return (
            <div key={config.type} className="card">
              <div className="card-content">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start gap-4">
                    <div 
                      className="p-3 rounded-lg"
                      style={{ backgroundColor: `${config.color}20` }}
                    >
                      <Icon 
                        className="h-6 w-6" 
                        style={{ color: config.color }}
                      />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-100 mb-1">
                        {config.title}
                      </h3>
                      <p className="text-sm text-gray-400">
                        {config.description}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-gray-400">
                      {block.enabled ? 'Active' : 'Inactive'}
                    </span>
                    <button
                      onClick={() => handleToggle(config.type)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        block.enabled ? 'bg-theme' : 'bg-gray-600'
                      }`}
                      style={block.enabled ? { backgroundColor: 'var(--theme-color)' } : {}}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          block.enabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Block Message
                  </label>
                  <div className="space-y-2">
                    <textarea
                      value={displayMessage}
                      onChange={(e) => handleMessageChange(config.type, e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-100 focus:outline-none focus:border-theme resize-none"
                      placeholder="Enter the message users will see when login is blocked..."
                    />
                    {isEditing && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleSaveMessage(config.type)}
                          className="px-4 py-2 bg-theme text-white rounded-lg hover:opacity-90 transition-opacity text-sm"
                          style={{ backgroundColor: 'var(--theme-color)' }}
                        >
                          Save Message
                        </button>
                        <button
                          onClick={() => {
                            setEditingMessage(prev => {
                              const updated = { ...prev };
                              delete updated[config.type];
                              return updated;
                            });
                          }}
                          className="px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors text-sm"
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {block.enabled && (
                  <div className="mt-4 p-3 bg-yellow-900/20 border border-yellow-700/50 rounded-lg">
                    <p className="text-sm text-yellow-400">
                      ⚠️ This block is currently active. Non-admin users cannot login.
                    </p>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="card">
        <div className="card-content">
          <div className="flex items-start gap-3">
            <Shield className="h-5 w-5 text-blue-400 mt-0.5" />
            <div>
              <h4 className="text-sm font-semibold text-gray-100 mb-1">Important Notes</h4>
              <ul className="text-sm text-gray-400 space-y-1 list-disc list-inside">
                <li>Admin accounts can always login regardless of these settings</li>
                <li>Multiple blocks can be active simultaneously</li>
                <li>The highest priority block message will be shown (Emergency → Security → Maintenance → Custom)</li>
                <li>Changes take effect immediately</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginBlocks;
